import {Box, Typography, Slider, TextField, InputAdornment, useMediaQuery} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useId } from "react";
import { useStoreDispatch, useStoreSelector } from "../store/hooks.ts";
import { enterNumericQuestionAnswer, selectCurrentNumericQuestionAnswers } from "../store/questionnaireSlice.ts";
import {theme} from "../theme.ts";

type SliderConfig = {
    inputId: string;
    label: string;
    min: number;
    max: number;
    step?: number;
    defaultValue?: number;
    minNormal?: number; // Optional
    maxNormal?: number; // Optional
    image?: string;
    additionalDisplay?: (value: number) => string;
};

type SlidersPageProps = {
    sliderConfigs: SliderConfig[];
};

export default function SlidersPage({ sliderConfigs }: SlidersPageProps) {
    const [sliderValues, setSliderValues] = useState<{ [key: string]: number | undefined }>({});
    const currentAnswers = useStoreSelector(selectCurrentNumericQuestionAnswers);
    const dispatch = useStoreDispatch();
    const [sliderValuesEmpty, setSliderValuesEmptyMap] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        setSliderValues(() => {
            const newValues: { [key: string]: number | undefined } = {};
            sliderConfigs.forEach((config) => {
                if (currentAnswers[config.inputId] !== undefined) {
                    newValues[config.inputId] = currentAnswers[config.inputId];
                    setSliderValuesEmptyMap((prevValues) => ({
                        ...prevValues,
                        [config.inputId]: false,
                    }));
                } else {
                    setSliderValuesEmptyMap((prevValues) => ({
                        ...prevValues,
                        [config.inputId]: true,
                    }));
                    newValues[config.inputId] = undefined;
                }
            });
            return newValues;
        });
    }, [sliderConfigs, currentAnswers]);

    const handleSliderChange = (inputId: string) => (_event: SliderChangeEvent, value: number | number[]) => {
        setSliderValues((prevValues) => ({
            ...prevValues,
            [inputId]: value as number,
        }));
        storeChange(inputId, value as number);
    };

    function storeChange(inputId: string, value: number) {
        dispatch(enterNumericQuestionAnswer([inputId, value]));
    }

    return (
        <Box>
            {sliderConfigs.map((config) => (
                <SliderComponent
                    key={config.inputId}
                    config={config}
                    value={sliderValuesEmpty[config.inputId] ? undefined : sliderValues[config.inputId]}
                    onChange={handleSliderChange(config.inputId)}
                />
            ))}
        </Box>
    );
}


type SliderChangeEvent = Event | React.ChangeEvent<HTMLInputElement> | null;


type SliderComponentProps = {
    config: SliderConfig;
    value: number | undefined;
    onChange: (_event: SliderChangeEvent, value: number | number[]) => void;
};


function SliderComponent({ config, value, onChange }: SliderComponentProps) {
    const { t } = useTranslation();
    const uniqueId = useId();

    const sliderValue =
        value !== undefined
            ? value
            : config.defaultValue !== undefined
                ? config.defaultValue
                : config.min;

    const [wasValueSetByUser, setWasValueSetByUser] = useState(false);
    const [textFieldValue, setTextFieldValue] = useState<string>(sliderValue.toString());
    const [isError, setIsError] = useState(false);
    const [changedBySlider, setChangedBySlider] = useState(true);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


    // Sync the text field with the slider value when the slider changes
    useEffect(() => {
        if (changedBySlider) {
            setTextFieldValue(sliderValue.toString());
            setIsError(false);
        }
    }, [sliderValue]);

    const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setTextFieldValue(inputValue);
        setChangedBySlider(false);

        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue)) {
            if (numericValue < config.min || numericValue > config.max) {
                setIsError(true);
                onChange(event, numericValue < config.min ? config.min : config.max);
            } else {
                setIsError(false);
                onChange(event, numericValue);
            }
        } else if (inputValue === "") {
            setIsError(true);
            onChange(event, config.min);
        }
    };

    const handleSliderChange = (_event: Event, newValue: number | number[]) => {
        const numericValue = newValue as number;
        setChangedBySlider(true);
        setTextFieldValue(numericValue.toString());
        setIsError(false);
        onChange(null, numericValue);

        setWasValueSetByUser(true);
    };

    const handleBlur = () => {
        const numericValue = parseFloat(textFieldValue);
        if (isNaN(numericValue) || numericValue < config.min) {
            setTextFieldValue(config.min.toString());
            setIsError(false);
            onChange(null, config.min);
        } else if (numericValue > config.max) {
            setTextFieldValue(config.max.toString());
            setIsError(false);
            onChange(null, config.max);
        }
        setWasValueSetByUser(true);
    };

    function showTextfieldValue() {
        if (wasValueSetByUser) {
            return true;
        } else {
            return value !== undefined;
        }
    }

    // Werte in einen 0-1 Bereich transformieren:
    const totalRange = config.max - config.min;
    const normalRangeStartPct = config.minNormal
        ? ((config.minNormal - config.min) / totalRange) * 100
        : 0;
    const normalRangeEndPct = config.maxNormal
        ? ((config.maxNormal - config.min) / totalRange) * 100
        : 100;

    // Gradient dynamisch berechnen:
    // Segment vor dem Normal-Bereich: transparent
    // Normal-Bereich: z.B. lightgrey
    // Segment nach dem Normal-Bereich: transparent
    const customTrackGradient = `
        linear-gradient(
            90deg,
            transparent ${normalRangeStartPct}%,
            black ${normalRangeStartPct}%,
            black ${normalRangeEndPct}%,
            transparent ${normalRangeEndPct}%
        )
    `;

    return (
        <Box
            sx={{
                mb: 2,
                position: "relative",
                width: "80%",
                margin: "0 auto",
            }}
        >
            <Typography
                variant="body1"
                align="center"
                gutterBottom
                id={`${uniqueId}-label`}
            >
                {t(config.label)+":"}
            </Typography>

            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 0,
                }}
            >
                <Slider
                    value={sliderValue}
                    onChange={handleSliderChange}
                    step={config.step}
                    min={config.min}
                    max={config.max}
                    valueLabelDisplay="auto"
                    aria-labelledby={`${uniqueId}-label`}
                    sx={{
                        flexGrow: 1,
                        mr: 2,
                        // color: "primary.main",
                        // Hier überschreiben wir die Track-Farbe, indem wir
                        // den eigentlichen Tracktransparent machen und im Hintergrund
                        // den Gradient anzeigen:
                        '& .MuiSlider-track': {
                            backgroundColor: (theme) => theme.palette.primary.main,
                        },
                        '& .MuiSlider-rail': {
                            backgroundImage: customTrackGradient,
                            backgroundSize: '100% 10px',
                            backgroundRepeat: 'no-repeat',
                            borderRadius: 4,
                        },
                    }}
                />

                <TextField
                    type="number"
                    value={showTextfieldValue() ? textFieldValue : ""}
                    onChange={handleTextFieldChange}
                    onBlur={handleBlur}
                    error={isError}
                    helperText={isError ? t('slider_error_out_of_range') : " "}
                    variant="outlined"
                    size="small"
                    sx={{
                        width: "110px",
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                borderColor: isError ? "red" : "default",
                            },
                        },
                    }}
                    inputProps={{
                        min: config.min,
                        max: config.max,
                        step: config.step,
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Typography variant="body1" sx={{ lineHeight: 1 }}>
                                    {t('slider_unit_cm')}
                                </Typography>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        </Box>
    );
}
